import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../config';
import axios from 'axios';
import SublyApi from '../helpers/Api';

// Thunks for form operations with real API implementations
export const getForms = createAsyncThunk(
	'formBuilder/getForms',
	async ({ token }, { rejectWithValue }) => {
		try {
			// If the API endpoint isn't implemented or returns 401/404, this will throw
			const response = await axios.get(`${API_BASE_URL}/api/admin/forms?limit=0`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			return response.data;
		} catch (error) {
			// Log the error for debugging
			console.warn(
				'Error fetching forms:',
				error.response?.status,
				error.message
			);

			// Determine if the API is not available (401/404 indicates API might not be implemented)
			const apiNotAvailable =
				error.response?.status === 401 || error.response?.status === 404;

			if (apiNotAvailable) {
				console.info(
					'The forms API endpoint may not be fully implemented yet.'
				);
			}

			const errorMessage =
				error.response?.data?.error?.message || 'Failed to fetch forms';

			// Include apiNotAvailable flag in the rejection value
			return rejectWithValue({
				message: errorMessage,
				apiNotAvailable: apiNotAvailable,
			});
		}
	}
);

export const getFieldTypes = createAsyncThunk(
	'formBuilder/getFieldTypes',
	async ({ token }, { rejectWithValue }) => {
		try {
			// Use the correct API endpoint for field types
			console.log('Fetching field types from API with correct endpoint');
			const response = await SublyApi.request(
				'api/admin/forms/field-types',
				{},
				'get',
				{ Authorization: `Bearer ${token}` }
			);
			console.log('Field types API response:', response);
			return response;
		} catch (error) {
			console.error('Error fetching field types:', error);

			// If API endpoint doesn't exist yet, use fallback field types
			console.warn('Field types API endpoint failed, using fallback data');

			// Fallback field types based on the documentation
			return {
				fieldTypes: [
					{ id: 'text', label: 'Text Field' },
					{ id: 'textarea', label: 'Text Area' },
					{ id: 'number', label: 'Number' },
					{ id: 'email', label: 'Email' },
					{ id: 'phone', label: 'Phone' },
					{ id: 'date', label: 'Date' },
					{ id: 'select', label: 'Dropdown' },
					{ id: 'radio', label: 'Radio Buttons' },
					{ id: 'checkbox', label: 'Checkboxes' },
					{ id: 'file', label: 'File Upload' },
					{ id: 'address', label: 'Address' },
					{ id: 'propertyAddress', label: 'Property Address' },
					{ id: 'mlsNumber', label: 'MLS Number' },
					{ id: 'clientInfo', label: 'Client Information' },
					{ id: 'agentInfo', label: 'Agent Information' },
					{ id: 'transactionDetails', label: 'Transaction Details' },
					{ id: 'giftPreferences', label: 'Gift Preferences' },
					{ id: 'movingDetails', label: 'Moving Details' },
					{ id: 'signature', label: 'Signature' },
				],
			};
		}
	}
);

export const getFormById = createAsyncThunk(
	'formBuilder/getFormById',
	async ({ id, token }, { rejectWithValue }) => {
		try {
			console.log(`Fetching form with ID: ${id}`);
			const response = await SublyApi.request(
				`api/admin/forms/${id}`,
				{},
				'get',
				{ Authorization: `Bearer ${token}` }
			);

			// Log the raw API response
			console.log('API Response for form:', response);

			// Extract form and fields data
			const form = response.form;
			let allFields = [];

			// Add top-level unassigned fields if they exist
			if (form.fields && Array.isArray(form.fields)) {
				console.log(`Found ${form.fields.length} unassigned fields`);
				allFields = [...form.fields];
			}

			// Process sections and their fields
			const sections = form.sections || [];
			console.log(`Found ${sections.length} sections`);

			// Check if sections have fields and add them to allFields
			sections.forEach((section) => {
				if (
					section.fields &&
					Array.isArray(section.fields) &&
					section.fields.length > 0
				) {
					console.log(
						`Found ${section.fields.length} fields in section ${section.id}`
					);
					// Mark fields with their section ID
					const sectionFields = section.fields.map((field) => ({
						...field,
						section_id: section.id,
					}));
					allFields = [...allFields, ...sectionFields];
				}
			});

			// Sort all fields by their global position
			allFields.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

			// Parse stringified JSONB fields (options, validation_rules)
			allFields = allFields.map((field) => {
				const parsedField = { ...field };

				// Parse options if it's a string
				if (typeof field.options === 'string' && field.options) {
					try {
						parsedField.options = JSON.parse(field.options);
					} catch (e) {
						console.error(`Error parsing options for field ${field.id}:`, e);
						parsedField.options = [];
					}
				} else if (!field.options) {
					parsedField.options = [];
				}

				// Parse validation_rules if it's a string
				if (
					typeof field.validation_rules === 'string' &&
					field.validation_rules
				) {
					try {
						parsedField.validation_rules = JSON.parse(field.validation_rules);
					} catch (e) {
						console.error(
							`Error parsing validation_rules for field ${field.id}:`,
							e
						);
						parsedField.validation_rules = {};
					}
				}

				return parsedField;
			});

			console.log(
				`Total fields found: ${allFields.length}, sorted by position:`,
				allFields.map((f) => ({
					id: f.id,
					label: f.label,
					position: f.position,
				}))
			);

			// Create field-section mapping
			const fieldSectionMap = [];
			sections.forEach((section) => {
				if (section.fields && Array.isArray(section.fields)) {
					section.fields.forEach((field) => {
						fieldSectionMap.push({
							field_id: field.id,
							section_id: section.id,
							position: field.position || 0,
						});
					});
				}
			});

			return {
				form,
				fields: allFields,
				sections,
				fieldSectionMap,
			};
		} catch (error) {
			const errorMessage =
				error.response?.data?.error?.message || 'Failed to fetch form';
			return rejectWithValue({ message: errorMessage });
		}
	}
);

export const createForm = createAsyncThunk(
	'formBuilder/createForm',
	async ({ formData, token }, { rejectWithValue }) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/api/admin/forms`,
				formData,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			return response.data;
		} catch (error) {
			const errorMessage =
				error.response?.data?.error?.message || 'Failed to create form';
			return rejectWithValue({ message: errorMessage });
		}
	}
);

export const updateForm = createAsyncThunk(
	'formBuilder/updateForm',
	async ({ id, formData, token }, { rejectWithValue }) => {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/api/admin/forms/${id}`,
				formData,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			return response.data;
		} catch (error) {
			const errorMessage =
				error.response?.data?.error?.message || 'Failed to update form';
			return rejectWithValue({ message: errorMessage });
		}
	}
);

export const deleteForm = createAsyncThunk(
	'formBuilder/deleteForm',
	async ({ id, token }, { rejectWithValue }) => {
		try {
			await axios.delete(`${API_BASE_URL}/api/admin/forms/${id}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			return { id };
		} catch (error) {
			const errorMessage =
				error.response?.data?.error?.message || 'Failed to delete form';
			return rejectWithValue({ message: errorMessage });
		}
	}
);

export const createFormField = createAsyncThunk(
	'formBuilder/createFormField',
	async ({ formId, field, sectionId, token }, { rejectWithValue }) => {
		try {
			console.log('Creating field for form:', formId, 'Field data:', field);

			// If field doesn't have a position, add one
			if (field.position === undefined) {
				field.position = 0; // Default position
			}

			const response = await SublyApi.request(
				`api/admin/forms/${formId}/fields`,
				field,
				'post',
				{ Authorization: `Bearer ${token}` }
			);

			console.log('API Response for createFormField:', response);

			// Extract the field from the response
			let createdField;
			if (response.field) {
				createdField = response.field;
				console.log('Field created successfully:', createdField);
			} else if (response.id) {
				// If the response is the field itself
				createdField = response;
				console.log(
					'Field created successfully (direct response):',
					createdField
				);
			} else {
				console.error('Unexpected API response format:', response);
				return rejectWithValue('Unexpected API response format');
			}

			// If a section ID was provided, associate the field with the section
			let mapping = null;
			if (sectionId) {
				try {
					console.log('Associating field with section:', sectionId);
					const mappingResponse = await SublyApi.request(
						`api/admin/forms/sections/${sectionId}/fields/${createdField.id}`,
						{ position: field.position || 0 },
						'post',
						{ Authorization: `Bearer ${token}` }
					);

					if (mappingResponse.sectionField) {
						mapping = mappingResponse.sectionField;
						console.log('Field-section mapping created:', mapping);
					} else {
						console.warn(
							'Unexpected mapping response format:',
							mappingResponse
						);
					}
				} catch (mappingError) {
					console.error('Error associating field with section:', mappingError);
					// Continue even if mapping fails - we still created the field
				}
			}

			return {
				field: createdField,
				mapping: mapping,
			};
		} catch (error) {
			console.error('Error creating field:', error);

			if (error.response) {
				console.error('Error response data:', error.response.data);
				console.error('Error response status:', error.response.status);
			}

			return rejectWithValue(error.message || 'Failed to create field');
		}
	}
);

export const updateFormField = createAsyncThunk(
	'formBuilder/updateFormField',
	async ({ formId, fieldId, field, token }, { rejectWithValue }) => {
		try {
			console.log('Updating field:', field);
			const res = await SublyApi.request(
				`api/admin/forms/fields/${fieldId}`,
				field,
				'put',
				{ Authorization: `Bearer ${token}` }
			);
			console.log('Field updated successfully:', res);
			return res;
		} catch (error) {
			console.error('Error updating field:', error);
			return rejectWithValue(error.message || 'Failed to update field');
		}
	}
);

export const deleteFormField = createAsyncThunk(
	'formBuilder/deleteFormField',
	async ({ fieldId, token }, { rejectWithValue }) => {
		try {
			const response = await SublyApi.request(
				`api/admin/forms/fields/${fieldId}`,
				{},
				'delete',
				{ Authorization: `Bearer ${token}` }
			);
			return { fieldId, response };
		} catch (error) {
			console.error('Error deleting field:', error);
			const message = Array.isArray(error) ? error[0] : (error.message || 'Failed to delete field');
			return rejectWithValue(message);
		}
	}
);

export const createFormSection = createAsyncThunk(
	'formBuilder/createFormSection',
	async ({ formId, sectionData, token }, { rejectWithValue }) => {
		try {
			console.log('Creating section for form:', formId, sectionData);
			const response = await SublyApi.request(
				`api/admin/forms/${formId}/sections`,
				sectionData,
				'post',
				{ Authorization: `Bearer ${token}` }
			);
			return response.section || response;
		} catch (error) {
			console.error('Error creating section:', error);
			return rejectWithValue(error.message || 'Failed to create section');
		}
	}
);

export const updateFormSection = createAsyncThunk(
	'formBuilder/updateFormSection',
	async ({ formId, sectionId, sectionData, token }, { rejectWithValue }) => {
		try {
			const response = await SublyApi.request(
				`api/admin/forms/${formId}/sections/${sectionId}`,
				sectionData,
				'put',
				{ Authorization: `Bearer ${token}` }
			);
			return response.section || response;
		} catch (error) {
			console.error('Error updating section:', error);
			return rejectWithValue(error.message || 'Failed to update section');
		}
	}
);

export const deleteFormSection = createAsyncThunk(
	'formBuilder/deleteFormSection',
	async ({ formId, sectionId, token }, { rejectWithValue }) => {
		try {
			const response = await SublyApi.request(
				`api/admin/forms/sections/${sectionId}`,
				{},
				'delete',
				{ Authorization: `Bearer ${token}` }
			);
			return { sectionId, response };
		} catch (error) {
			console.error('Error deleting section:', error);
			return rejectWithValue(error.message || 'Failed to delete section');
		}
	}
);

export const reorderFormFields = createAsyncThunk(
	'formBuilder/reorderFormFields',
	async (
		{ formId, sectionId, fieldPositions, token },
		{ rejectWithValue, getState }
	) => {
		try {
			if (sectionId) {
				// If we're reordering within a section, use the section reorder endpoint
				const response = await axios.put(
					`${API_BASE_URL}/api/admin/forms/sections/${sectionId}/reorder`,
					{ fieldPositions },
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				);
				return {
					fieldPositions,
					sectionId,
					...response.data,
				};
			} else {
				// For unassigned fields, update each field's position individually
				// Get the current fields from the Redux state to include required fields
				const { formFields } = getState().formBuilder;

				const updatePromises = fieldPositions.map(({ fieldId, position }) => {
					// Find the complete field data to include required fields
					const field = formFields.find((f) => f.id === fieldId);

					if (!field) {
						console.error(`Field with ID ${fieldId} not found in state`);
						return Promise.reject(
							`Field with ID ${fieldId} not found in state`
						);
					}

					// Send all required fields plus the new position
					return axios.put(
						`${API_BASE_URL}/api/admin/forms/fields/${fieldId}`,
						{
							field_key: field.field_key,
							type: field.type,
							label: field.label,
							position,
							description: field.description || '',
							is_required: field.is_required || false,
							placeholder: field.placeholder || '',
						},
						{
							headers: {
								Authorization: `Bearer ${token}`,
							},
						}
					);
				});

				await Promise.all(updatePromises);

				// Return the new positions for the reducer to update state
				return {
					fieldPositions,
					sectionId: null,
				};
			}
		} catch (error) {
			console.error('Error during reorderFormFields:', error);
			console.error('Response data:', error.response?.data);
			const errorMessage =
				error.response?.data?.error?.message || 'Failed to reorder fields';
			return rejectWithValue({ message: errorMessage });
		}
	}
);

// Add a new thunk for assigning a field to a section
export const assignFieldToSection = createAsyncThunk(
	'formBuilder/assignFieldToSection',
	async (
		{ formId, fieldId, sectionId, position, token },
		{ rejectWithValue }
	) => {
		try {
			const response = await SublyApi.request(
				`api/admin/forms/sections/${sectionId}/fields/${fieldId}`,
				{ position },
				'post',
				{ Authorization: `Bearer ${token}` }
			);
			return response.sectionField || response;
		} catch (error) {
			console.error('Error assigning field to section:', error);
			return rejectWithValue(
				error.message || 'Failed to assign field to section'
			);
		}
	}
);

// Add a new thunk for updating a field's position within a section
export const updateFieldPosition = createAsyncThunk(
	'formBuilder/updateFieldPosition',
	async (
		{ formId, fieldId, sectionId, position, token },
		{ rejectWithValue }
	) => {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/api/admin/forms/sections/${sectionId}/fields/${fieldId}/position`,
				{ position },
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			return response.data;
		} catch (error) {
			const errorMessage =
				error.response?.data?.error?.message ||
				'Failed to update field position';
			return rejectWithValue({ message: errorMessage });
		}
	}
);

export const removeFieldFromSectionThunk = createAsyncThunk(
	'formBuilder/removeFieldFromSection',
	async ({ formId, fieldId, sectionId, token }, { rejectWithValue }) => {
		try {
			const response = await SublyApi.request(
				`api/admin/forms/sections/${sectionId}/fields/${fieldId}`,
				{},
				'delete',
				{ Authorization: `Bearer ${token}` }
			);
			return { fieldId, sectionId, response };
		} catch (error) {
			console.error('Error removing field from section:', error);
			return rejectWithValue(
				error.message || 'Failed to remove field from section'
			);
		}
	}
);

// Slice definition
const formBuilderSlice = createSlice({
	name: 'formBuilder',
	initialState: {
		// If you're seeing forms in the UI without a working API, they come from this array.
		// Once the API endpoint is working, this should be an empty array.
		forms: [], // Remove mock data and leave this as an empty array when API is ready
		currentForm: null,
		formFields: [],
		formSections: [],
		fieldSectionMap: [],
		fieldTypes: [],
		status: 'idle',
		error: null,
		apiNotAvailable: false, // Flag to indicate if the API is not available
	},
	reducers: {
		clearCurrentForm: (state) => {
			state.currentForm = null;
			state.formFields = [];
			state.formSections = [];
			state.fieldSectionMap = [];
		},
		setFieldPositionInSection: (state, action) => {
			const { fieldId, sectionId, position } = action.payload;

			const mappingIndex = state.fieldSectionMap.findIndex(
				(mapping) =>
					mapping.field_id === fieldId && mapping.section_id === sectionId
			);

			if (mappingIndex !== -1) {
				state.fieldSectionMap[mappingIndex].position = position;
			}
		},
		removeFieldFromSection: (state, action) => {
			const { fieldId, sectionId } = action.payload;

			state.fieldSectionMap = state.fieldSectionMap.filter(
				(mapping) =>
					!(mapping.field_id === fieldId && mapping.section_id === sectionId)
			);
		},
		updateFieldsOrder: (state, action) => {
			// Update formFields with the new order
			state.formFields = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			// Loading states
			.addCase(getForms.pending, (state) => {
				state.status = 'loading';
				state.error = null;
			})
			.addCase(getFormById.pending, (state) => {
				state.status = 'loading';
				state.error = null;
			})
			.addCase(createForm.pending, (state) => {
				state.status = 'loading';
				state.error = null;
			})
			.addCase(updateForm.pending, (state) => {
				state.status = 'loading';
				state.error = null;
			})
			.addCase(deleteForm.pending, (state) => {
				state.status = 'loading';
				state.error = null;
			})

			// Success states
			.addCase(getForms.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.forms = action.payload.forms;
			})
			.addCase(getFormById.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.error = null;
				state.currentForm = action.payload.form;
				state.formFields = action.payload.fields;
				state.formSections = action.payload.sections;
				state.fieldSectionMap = action.payload.fieldSectionMap;
			})
			.addCase(createForm.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.forms.push(action.payload.form);
			})
			.addCase(updateForm.fulfilled, (state, action) => {
				state.status = 'succeeded';
				const index = state.forms.findIndex(
					(form) => form.id === action.payload.form.id
				);
				if (index !== -1) {
					state.forms[index] = action.payload.form;
				}
				if (
					state.currentForm &&
					state.currentForm.id === action.payload.form.id
				) {
					state.currentForm = action.payload.form;
				}
			})
			.addCase(deleteForm.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.forms = state.forms.filter(
					(form) => form.id !== action.payload.id
				);
				if (state.currentForm && state.currentForm.id === action.payload.id) {
					state.currentForm = null;
				}
			})

			// Field Operations
			.addCase(createFormField.pending, (state) => {
				state.status = 'loading';
				state.error = null;
			})
			.addCase(createFormField.fulfilled, (state, action) => {
				console.log('createFormField fulfilled with payload:', action.payload);
				state.status = 'succeeded';

				// Make sure we have a valid field from the payload
				if (action.payload.field) {
					// Add the field to formFields array
					state.formFields = [
						...(state.formFields || []),
						action.payload.field,
					];
					console.log(
						'Added field to formFields. New count:',
						state.formFields.length
					);

					// Add mapping if it exists
					if (action.payload.mapping) {
						state.fieldSectionMap = [
							...(state.fieldSectionMap || []),
							action.payload.mapping,
						];
						console.log('Added field-section mapping:', action.payload.mapping);
					}

					// Also update the currentForm fields if they exist
					if (state.currentForm) {
						state.currentForm = {
							...state.currentForm,
							fields: [
								...(state.currentForm.fields || []),
								action.payload.field,
							],
						};
						console.log(
							'Updated currentForm fields. New count:',
							state.currentForm.fields.length
						);
					}
				} else {
					console.warn(
						'createFormField response does not contain a field:',
						action.payload
					);
				}
			})
			.addCase(updateFormField.fulfilled, (state, action) => {
				state.status = 'succeeded';
				const updatedField = action.payload;
				state.formFields = state.formFields.map((field) =>
					field.id === updatedField.id ? updatedField : field
				);
				if (state.currentForm) {
					state.currentForm = {
						...state.currentForm,
						fields: state.currentForm.fields.map((field) =>
							field.id === updatedField.id ? updatedField : field
						),
					};
				}
			})
			.addCase(deleteFormField.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.formFields = state.formFields.filter(
					(field) => field.id !== action.payload.fieldId
				);
				// Also remove from fieldSectionMap
				state.fieldSectionMap = state.fieldSectionMap.filter(
					(mapping) => mapping.field_id !== action.payload.fieldId
				);
			})

			// Section Operations
			.addCase(createFormSection.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.formSections.push(action.payload);
			})
			.addCase(updateFormSection.fulfilled, (state, action) => {
				state.status = 'succeeded';
				const index = state.formSections.findIndex(
					(section) => section.id === action.payload.section.id
				);
				if (index !== -1) {
					state.formSections[index] = action.payload.section;
				}
			})
			.addCase(deleteFormSection.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.formSections = state.formSections.filter(
					(section) => section.id !== action.payload.sectionId
				);
				// Also remove all fields associated with this section
				state.fieldSectionMap = state.fieldSectionMap.filter(
					(mapping) => mapping.section_id !== action.payload.sectionId
				);
			})

			// Mapping Operations
			.addCase(assignFieldToSection.fulfilled, (state, action) => {
				state.status = 'succeeded';
				// The payload is the sectionField object directly (not wrapped in 'mapping')
				const newMapping = action.payload;
				if (newMapping && newMapping.field_id) {
					// Remove any existing mapping for this field
					state.fieldSectionMap = state.fieldSectionMap.filter(
						(mapping) => mapping.field_id !== newMapping.field_id
					);
					// Add the new mapping
					state.fieldSectionMap.push(newMapping);
				}
			})
			.addCase(updateFieldPosition.fulfilled, (state, action) => {
				state.status = 'succeeded';
				// The payload could be the mapping directly or wrapped
				const updatedMapping = action.payload.mapping || action.payload;
				if (updatedMapping && updatedMapping.field_id) {
					// Find and update the mapping
					const mappingIndex = state.fieldSectionMap.findIndex(
						(mapping) =>
							mapping.field_id === updatedMapping.field_id &&
							mapping.section_id === updatedMapping.section_id
					);
					if (mappingIndex !== -1) {
						state.fieldSectionMap[mappingIndex] = updatedMapping;
					}
				}
			})
			.addCase(reorderFormFields.fulfilled, (state, action) => {
				state.status = 'succeeded';
				const { fieldPositions, sectionId } = action.payload;

				// Update positions in both fieldSectionMap AND formFields
				if (fieldPositions && Array.isArray(fieldPositions)) {
					fieldPositions.forEach(({ id, position }) => {
						// Update fieldSectionMap
						const mappingIndex = state.fieldSectionMap.findIndex(
							(mapping) =>
								mapping.field_id === id &&
								(sectionId ? mapping.section_id === sectionId : true)
						);
						if (mappingIndex !== -1) {
							state.fieldSectionMap[mappingIndex].position = position;
						}

						// Also update formFields (this is what the UI reads from)
						const fieldIndex = state.formFields.findIndex(
							(field) => field.id === id
						);
						if (fieldIndex !== -1) {
							state.formFields[fieldIndex].position = position;
						}
					});
				}
			})

			// Field types
			.addCase(getFieldTypes.pending, (state) => {
				state.status = 'loading';
				state.error = null;
			})
			.addCase(getFieldTypes.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.fieldTypes = action.payload.fieldTypes;
			})
			.addCase(getFieldTypes.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload?.message || 'Failed to fetch field types';
			})

			// Error states
			.addCase(getForms.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload?.message || 'Failed to fetch forms';

				// If API is not available, indicate this in the state
				if (action.payload?.apiNotAvailable) {
					state.apiNotAvailable = true;
				}
			})
			.addCase(getFormById.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload || 'Failed to fetch form';
				console.error('getFormById rejected with error:', state.error);
			})
			.addCase(createForm.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload?.message || 'Failed to create form';
			})
			.addCase(updateForm.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload?.message || 'Failed to update form';
			})
			.addCase(deleteForm.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload?.message || 'Failed to delete form';
			})
			.addCase(createFormField.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload || 'Failed to create field';
				console.error('createFormField rejected with error:', state.error);
			})
			.addCase(updateFormField.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload?.message || 'Failed to update field';
			})
			.addCase(deleteFormField.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload?.message || 'Failed to delete field';
			})
			.addCase(createFormSection.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload?.message || 'Failed to create section';
			})
			.addCase(updateFormSection.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload?.message || 'Failed to update section';
			})
			.addCase(deleteFormSection.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload?.message || 'Failed to delete section';
			})
			.addCase(assignFieldToSection.rejected, (state, action) => {
				state.status = 'failed';
				state.error =
					action.payload?.message || 'Failed to assign field to section';
			})
			.addCase(updateFieldPosition.rejected, (state, action) => {
				state.status = 'failed';
				state.error =
					action.payload?.message || 'Failed to update field position';
			})
			.addCase(reorderFormFields.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload?.message || 'Failed to reorder fields';
			})
			.addCase(removeFieldFromSectionThunk.fulfilled, (state, action) => {
				state.status = 'succeeded';
				const { fieldId, sectionId } = action.payload;
				// Remove the mapping from fieldSectionMap
				state.fieldSectionMap = state.fieldSectionMap.filter(
					(mapping) =>
						!(mapping.field_id === fieldId && mapping.section_id === sectionId)
				);
			})
			.addCase(removeFieldFromSectionThunk.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload || 'Failed to remove field from section';
			});
	},
});

export const {
	clearCurrentForm,
	setFieldPositionInSection,
	removeFieldFromSection,
	updateFieldsOrder,
} = formBuilderSlice.actions;

export default formBuilderSlice.reducer;
